import { config } from '@ps/config/client';
import { auth } from '@ps/functions/firebase/auth';
import type { PopStoreUserSubscriptionInfo } from '@ps/models/lib/creator-subscription';

/**
 * Shape of the `checkUserSubscriptionStatus` AWS creator endpoint response we
 * consume. The endpoint reports the signed-in user's subscription standing with
 * one creator: their highest-tier subscription, any queued (future) change, and
 * the full list. Migrated from PopshopRN
 * `src/functions/popstore/checkUserSubscriptionStatus.ts`.
 */
export interface CheckUserSubscriptionStatusData {
  user_id: string;
  subscriber: boolean;
  total_subscriptions: number;
  top_level_subscription: PopStoreUserSubscriptionInfo | null | undefined;
  subscriptions: PopStoreUserSubscriptionInfo[];
  queued_subscription: PopStoreUserSubscriptionInfo | null | undefined;
}

interface CheckUserSubscriptionStatusResponse {
  message: string;
  data: CheckUserSubscriptionStatusData | null;
}

/**
 * Fetch the signed-in user's subscription standing with one creator. Returns
 * `null` when unauthenticated or when the endpoint reports no data. Bespoke AWS
 * creator endpoint (not in the generated client), mirroring the other
 * subscription AWS calls (see `checkout/functions/subscriptionPayment.ts`).
 */
export async function checkUserSubscriptionStatus(args: {
  userId: string;
  creatorId: string;
  signal?: AbortSignal;
}): Promise<CheckUserSubscriptionStatusData | null> {
  const token = await auth().currentUser?.getIdToken();
  if (!token) return null;

  const url = new URL(
    `${config.awsCreatorApiBaseUrl}/checkUserSubscriptionStatus`,
  );
  url.searchParams.set('creator_store_id', args.creatorId);
  url.searchParams.set('user_id', args.userId);

  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: token, 'Content-Type': 'application/json' },
    signal: args.signal,
  });
  if (!response.ok) {
    throw new Error(`checkUserSubscriptionStatus failed: ${response.status}`);
  }
  const body = (await response.json()) as CheckUserSubscriptionStatusResponse;
  return body.data;
}
